// ─────────────────────────────────────────────
//  caseBarLabel — WHAT a Timeline case bar prints, and what it gives up first
//  when the bar is too narrow to print all of it.
//
//  ⚠️ THE NAME IS THE PAYLOAD. Everything else on a case bar — the departure,
//  the shift code, the role suffix, the surgeon — is an annotation about a
//  person the reader has already identified. A bar that prints
//  "Thomas Klaph…" has spent its whole width on the one thing it failed to
//  deliver. The product owner reported exactly that (2026-08-18, screenshot of
//  a short case in the Timeline chart), and the cause is arithmetic, not
//  styling: `s.bar` is `position:absolute` with `minWidth: 40` and
//  `overflow: hidden`, so a 30-minute case on a 10-hour axis is drawn ~40px
//  wide and the browser simply clips whatever does not fit.
//
//  So the bar DEGRADES on purpose instead of being clipped by accident. The
//  ladder gives up annotation before identity, and identity from the least
//  identifying end:
//
//    L0  "Thomas Klaphake, CRNA 3 PM"   everything
//    L1  "Thomas Klaphake 3 PM"         drop the role suffix
//    L2  "Thomas Klaphake"              drop the departure / shift code
//    L3  "T. Klaphake"                  first name → initial
//    L4  "Klaphake"                     surname alone
//    L5  "Klaphak…"                     CSS ellipsis — the floor, not a rung
//
//  A surname is never shortened while anything above it is still on the bar.
//  L5 is not chosen by this module; it is what `text-overflow: ellipsis` does
//  to L4 when even a bare surname will not fit, and it is reported (fits:false)
//  so a caller can react.
//
//  ⚠️ NOTHING DROPPED HERE IS LOST. The bar's `title` and its accessible name
//  are built from the UNDEGRADED text (`caseBarSpeech`), so the tooltip and the
//  screen reader always carry the full names, both departures, and the surgeon
//  / supervising MD — whatever the pixels could show.
//
//  Pure: no React, no DOM measuring. jsdom has no layout, so a width measured
//  inside a component is a width that cannot be tested; the caller passes the
//  measured px in and everything here is arithmetic.
// ─────────────────────────────────────────────

import { isNameCandidate, nameWordsOf } from "./providerCell";

// Average advance width of one character, in px, for the two bar type styles.
//
// ⚠️ AN ESTIMATE, DELIBERATELY. The alternative is `canvas.measureText`, which
// jsdom does not implement, or a hidden DOM probe, which cannot run before the
// first paint — and the bar has to decide what to draw on that first paint.
// Measured against the system sans stack the app ships: 11px/700 averages
// ~6.2px per character over CRNA-name text, 10px/400 averages ~5.4px. Both are
// rounded UP, so the ladder gives up slightly early rather than clipping.
export const LABEL_CHAR_PX = 6.2;
export const SUB_CHAR_PX = 5.4;

// `s.bar` padding is "0 6px".
export const BAR_PADDING_PX = 12;

// Below this a sub-line (procedure · surgeon · ⚕ MD) can hold nothing a reader
// could use — roughly four characters after padding — so it is dropped whole
// rather than printed as an ellipsis. This is the "then the surgeon" rung.
export const SUB_MIN_PX = 46;

// A trailing role suffix the sheet importer writes onto provider names
// ("Josh Larson, CRNA"). It is an annotation: the Timeline's provider lane is
// already the CRNA/AA lane, and the room card prints the role in full.
const ROLE_SUFFIX_RE = /\s*,\s*(CRNAs?|AAs?|SRNAs?|RRNAs?|SAAs?|CAAs?|RNs?|MDs?|DOs?)\.?\s*$/i;

// Surname particles that belong TO the surname: "Van Der Berg", "de Souza".
const PARTICLES = new Set([
  "van", "von", "de", "del", "della", "da", "di", "du", "la", "le", "los", "las",
  "den", "der", "ten", "ter", "st", "st.", "mac", "mc", "bin", "ibn", "al",
]);

// "Josh Larson, CRNA" → "Josh Larson". Leaves a name with no suffix untouched.
export function stripRoleSuffix(name) {
  return String(name || "").replace(ROLE_SUFFIX_RE, "").trim();
}

const tokensOf = (name) => stripRoleSuffix(name).split(/\s+/).filter(Boolean);

// ── THE LAST WORD IS NOT ALWAYS THE SURNAME ──────────────
//
// ⚠️ THE FAILURE THIS EXISTS TO STOP: A NAME NOBODY RECOGNISES.
// A provider label comes off the daily sheet with the sheet's own bookkeeping
// still attached — "K. Grant 7 REQ", "Chris Glenn 7 (relief)". Taking the last
// token as the surname printed **"K. REQ"** on a narrow bar: a scheduling
// annotation wearing a person's shape. On a wall board that is worse than
// truncating, because "Klaphak…" announces itself as incomplete and "REQ" does
// not — a reader cannot tell it is wrong.
//
// ⚠️ THE VOCABULARY IS domain/providerCell's, DELIBERATELY, AND SO IS THE PEEL.
// That module already owns "which words in a provider cell are not part of the
// name" — SHIFT_CODES via `isShiftToken`, the routing/bookkeeping words, the
// annotation tail. A second copy here would drift the first time the department
// adds a shift or the sheet learns a new marker, which is the exact shape this
// repo keeps getting burned by. This file asks the question; it does not answer
// it, and `nameWordsOf` documents the two floors the peel stops at.

// The surname out of tokens already peeled, particles included.
function surnameFromTokens(t) {
  if (t.length <= 1) return t[0] || "";
  let i = t.length - 1;
  // Walk back over particles, but never past the second token: "Van Der Berg"
  // as a whole name must still degrade to something shorter than itself.
  while (i > 1 && PARTICLES.has(t[i - 1].toLowerCase())) i -= 1;
  return t.slice(i).join(" ");
}

// The surname alone, particles included. A single-token name IS the surname —
// returning "" there would blank the bar for a person the sheet named only once.
//
// ⚠️ AND A CELL THAT IS *ONLY* ANNOTATIONS KEEPS ITS TEXT. "REQ 7" names nobody,
// but a blank where a name belongs is the one outcome worse than an odd one: the
// reader cannot even tell a room has a problem. Peel nothing, print it whole,
// and let a human see the garbage the sheet actually contains.
export function surnameOf(name) {
  const all = tokensOf(name);
  if (all.length <= 1) return all[0] || "";
  if (!all.some(isNameCandidate)) return all.join(" ");
  return surnameFromTokens(nameWordsOf(all.join(" ")));
}

// "Thomas Klaphake" → "T. Klaphake". Only the FIRST token is initialled; a
// middle name is dropped with it, because the surname is what identifies the
// person on a board where everyone's first name is already spoken aloud.
export function initialledName(name) {
  const all = tokensOf(name);
  if (all.length <= 1) return all[0] || "";
  if (!all.some(isNameCandidate)) return all.join(" ");
  const t = nameWordsOf(all.join(" "));
  if (t.length <= 1) return t[0] || "";
  const first = t[0];
  const initial = first[0] ? `${first[0].toUpperCase()}.` : "";
  const rest = surnameFromTokens(t);
  if (!initial) return rest;
  return `${initial} ${rest}`.trim();
}

// Width of a rendered string, in px, at the given per-character estimate.
export function textPx(text, charPx) {
  return String(text || "").length * charPx;
}

// One rung of the ladder, rendered.
const rungText = (name, depart) => (depart ? `${name} ${depart}` : name);

/**
 * Pick the widest rung of the ladder that fits.
 *
 * @param name      the person's name as stored ("Josh Larson, CRNA")
 * @param depart    their departure label ("3 PM") or "" — an annotation
 * @param budgetPx  usable px inside the bar, or null/0 when unmeasured
 * @param charPx    per-character estimate for this line's type style
 * @returns { name, depart, level, fits }
 *          `level` 0-4 per the ladder above; `fits` false means even L4 is
 *          wider than the bar and the browser's ellipsis will finish the job.
 */
export function fitBarName({ name, depart = "", budgetPx = null, charPx = LABEL_CHAR_PX }) {
  const raw = String(name || "").trim();
  const bare = stripRoleSuffix(raw);
  const dep = String(depart || "").trim();
  const ladder = [
    { name: raw, depart: dep, level: 0 },
    { name: bare, depart: dep, level: 1 },
    { name: bare, depart: "", level: 2 },
    { name: initialledName(bare), depart: "", level: 3 },
    { name: surnameOf(bare), depart: "", level: 4 },
  ];
  // ⚠️ UNMEASURED MEANS "ASSUME THERE IS ROOM", the same reading `CaseBar`
  // takes of a zero `trackWidth`. Degrading on a width nobody measured would
  // abbreviate every name on the first paint of every board.
  if (!(budgetPx > 0)) return { ...ladder[0], fits: true };
  for (const rung of ladder) {
    if (textPx(rungText(rung.name, rung.depart), charPx) <= budgetPx) {
      return { ...rung, fits: true };
    }
  }
  return { ...ladder[ladder.length - 1], fits: false };
}

// The gap between the seat holder's name and the trainee's chip beside it, in
// px. `caseBarStyles.barNameLine` sets the same number as a flex `gap`; the two
// have to agree or the budget below is measuring a line the bar does not draw.
export const INLINE_CHIP_GAP_PX = 4;

/**
 * The trainee's chip that rides on the seat holder's bar, fitted to what is
 * left of the bar AFTER the seat holder's own name has taken its width.
 *
 * ⚠️ THE SEAT'S NAME IS NOT NEGOTIABLE, AND THIS IS WHERE THAT IS ENFORCED.
 * The chip used to be a `<span>` INSIDE the seat's label box, so `text-overflow:
 * ellipsis` clipped it first and the name was safe by accident. It is a control
 * of its own now — a second flex item on the same line — and a control that is
 * merely clipped is a control nobody can press. So a chip that does not fit is
 * DROPPED WHOLE rather than drawn as a stub: `caseBarSpeech` still announces the
 * person, the bar's `title` still names them, and the ORIENTEES card carries
 * them with their room and their hour. A fact displaced off the bar is not a
 * fact dropped from the page.
 *
 * ⚠️ THE WORD "orientee" NEVER DEGRADES. The name shortens — full, initialled,
 * surname — and the mark stays on every rung, because the mark is the whole
 * reason the chip is legible as a trainee and not as a second seat holder.
 *
 * @param name      the trainee's name as stored
 * @param seat      { name, depart } — the FITTED seat label already on the line
 * @param budgetPx  usable px inside the bar (`labelBudgetPx`), or null
 * @returns { text, fits } — `text` "" means there was no room; draw nothing.
 */
export function fitInlineChip({ name, seat = null, budgetPx = null, charPx = LABEL_CHAR_PX }) {
  const bare = stripRoleSuffix(String(name || "").trim());
  if (!bare) return { text: "", fits: false };
  const mark = (n) => `+ ${n} ·orientee`;
  // Same ladder as `fitBarName` rungs 2-4, and asked of the same helpers rather
  // than re-cut here: a second copy would abbreviate "Van Der Berg" differently
  // from the name three pixels to its left.
  const ladder = [bare, initialledName(bare), surnameOf(bare)].filter(Boolean);
  // Unmeasured means "assume there is room", the same reading every other fit in
  // this file takes of a zero width.
  if (!(budgetPx > 0)) return { text: mark(ladder[0]), fits: true };
  const used = textPx(rungText(seat?.name || "", seat?.depart || ""), charPx);
  const room = budgetPx - used - INLINE_CHIP_GAP_PX;
  for (const n of ladder) {
    const t = mark(n);
    if (textPx(t, charPx) <= room) return { text: t, fits: true };
  }
  return { text: "", fits: false };
}

/**
 * Usable px inside a bar of `barPx` total width, or null when unmeasured.
 * Padding comes off; the drag handles do NOT, because they are drawn OVER the
 * label at 55% opacity rather than beside it — subtracting 48px would abbreviate
 * every bar under ~100px for space the text is in fact allowed to use.
 */
export function labelBudgetPx(barPx) {
  if (!(barPx > 0)) return null;
  return Math.max(0, barPx - BAR_PADDING_PX);
}

/**
 * The two people a case bar shows.
 *
 * `seat` is the provider who holds the room (`ahpId`); `second` is the other
 * person in it when there is one.
 *
 * ⚠️ THE NAMES ARE A SLOT, NOT A RANK. The seat/second distinction survives here
 * because a WRITE has to know which slot it is updating, and `isSoloCase` still
 * asks who holds the room. It is not a display hierarchy: the owner removed
 * that on 2026-08-20 — *"i dont care who is orienting who, place the 2 crnas."*
 * Both are drawn the same, both are spoken the same, and nothing downstream may
 * reintroduce an indent, a mark or the word "orienting" for one of them.
 *
 * `sub` (procedure · surgeon · ⚕ MD) is returned whatever the room holds: a
 * paired bar grows a band for it rather than giving up its line.
 */
export function caseBarPeople(c) {
  const seat = {
    name: String(c?.label || "").trim(),
    depart12h: String(c?.depart12h || "").trim(),
  };
  const o = c?.orientee;
  const second =
    o && String(o.name || "").trim()
      ? { name: String(o.name).trim(), depart12h: String(o.depart12h || "").trim() }
      : null;
  // ⚠️ A DIFFERENT THING FROM `second`, ON PURPOSE. `second` is the lower BAND
  // of a paired room, and the even-handedness rule above governs it: two CRNAs
  // drawn the same, no indent, no mark, no word "orienting".
  //
  // `inlineSecond` is the timeline's own case, and the owner reversed the
  // display rule for it on 2026-09-07: the trainee is to be visible in the room
  // again, as a mark on the ONE bar rather than a second band that regrows the
  // row. `TimelineChartPage.barCase` strips `orientee` (which is what holds the
  // row at 32px) and hands the person over on `inlineOrientee` instead. Nothing
  // that measures, lays out or counts reads this field.
  const io_ = c?.inlineOrientee;
  const inlineSecond =
    io_ && String(io_.name || "").trim() ? { name: String(io_.name).trim() } : null;
  return { seat, second, inlineSecond, sub: String(c?.sub || "").trim() };
}

// A departure as a sentence fragment. An UNKNOWN departure says nothing at all:
// each provider's leaving time is their own, and borrowing the other's would
// print a confident wrong answer about when a room loses a person.
const leaves = (d) => (d ? `, leaves ${d}` : "");

/**
 * The bar's full spoken form — the accessible name, and the source of truth the
 * visible label degrades away from.
 *
 * ⚠️ THIS MUST NAME BOTH PEOPLE. A screen-reader user gets no tooltip and no
 * hatch; this sentence is the whole bar. It is built from the undegraded names,
 * so a 40px bar reading "Klaphake" still announces "Thomas Klaphake".
 *
 * ⚠️ AND IT MUST NAME THEM EVENLY. It used to say "with Kayla Gibbons,
 * orienting" — a relationship the owner has since removed from the display
 * entirely: *"i dont care who is orienting who, place the 2 crnas."* A reader
 * has to hear what a sighted user sees, so both people are announced in the
 * same shape, preceded by a count so the listener knows a second name is
 * coming. Neither is described in terms of the other.
 */
export function caseBarSpeech(c, roomName) {
  const { seat, second, inlineSecond, sub } = caseBarPeople(c);
  const parts = [];
  const where = String(roomName || "").trim();
  const when = c?.start12h && c?.end12h ? `${c.start12h} to ${c.end12h}` : "";
  const head = [where, when].filter(Boolean).join(", ");
  if (head) parts.push(head);
  if (second) parts.push("Two providers");
  if (seat.name) parts.push(`${stripRoleSuffix(seat.name)}${leaves(seat.depart12h)}`);
  if (second) parts.push(`${stripRoleSuffix(second.name)}${leaves(second.depart12h)}`);
  // ⚠️ THE CHIP IS VISUAL; THIS IS THE ONLY WAY A READER GETS THE TRAINEE. The
  // suffix on the bar is inside an ellipsised box and carries no semantics at
  // all, so a person standing in the room would otherwise exist for sighted
  // users only. The word is spoken because here it is NOT decoration: it is
  // what stops a listener hearing a second seat holder.
  //
  // No leaving time. The inline person is carried for display and their hour is
  // not resolved onto this object; announcing one would be an invented fact.
  if (inlineSecond) parts.push(`With ${stripRoleSuffix(inlineSecond.name)}, orientee`);
  if (sub) parts.push(sub);
  return parts.join(". ");
}
